import fp from "lodash/fp";

import { uiConstants } from "./ui.constants";
import { getModals, getAlerts } from "./ui.reducer";

export const uiActions = {
	hideModal,
	hideNoConnectionModal,
	hideNotification,
	hideServerErrorModal,
	hideSessionExpirationModal,
	hideSpinnerOverlay,
	setIsOnline,
	showErrorNotification,
	showInfoNotification,
	showModal,
	showNoConnectionModal,
	showNotification,
	showServerErrorModal,
	showSessionExpirationModal,
	showSpinnerOverlay,
	showSuccessNotification,
	showSuccessNotificationEnsureSingleInstance,
	showWarningNotification,
};

function showNotification({ title, variant }) {
	return {
		type: uiConstants.SHOW_ALERT,
		title,
		variant,
	};
}

function hideNotification(title) {
	return {
		type: uiConstants.HIDE_ALERT,
		title,
	};
}

function showSuccessNotification({ title, delay = 3000 }) {
	return dispatch => {
		dispatch(showNotification({ title, variant: "success" }));
		setTimeout(() => dispatch(hideNotification(title)), delay);
	};
}

function showSuccessNotificationEnsureSingleInstance({ title, delay = 3000 }) {
	return (dispatch, getState) => {
		const state = getState();
		const modals = getAlerts(state)?.filter(
			modal => modal.variant === "success",
		);
		if (modals?.length > 0) {
			dispatch(hideNotification(modals[0].title));
		}
		showSuccessNotification({ title, delay })(dispatch);
	};
}

function showWarningNotification({ title, delay = 3000 }) {
	return dispatch => {
		dispatch(showNotification({ title, variant: "warning" }));
		setTimeout(() => dispatch(hideNotification(title)), delay);
	};
}

function showInfoNotification({ title, delay = 3000 }) {
	return dispatch => {
		dispatch(showNotification({ title, variant: "success" }));
		setTimeout(() => dispatch(hideNotification(title)), delay);
	};
}

function showErrorNotification({ title, delay = 3000 }): any {
	return dispatch => {
		const parsedTitle =
			typeof title === "string"
				? title
				: "Problem encountered. Please try again";

		dispatch(showNotification({ title: parsedTitle, variant: "failure" }));
		setTimeout(() => dispatch(hideNotification(parsedTitle)), delay);
	};
}

function showSpinnerOverlay(
	isOverlayTransparent = true,
	spinnerOverlayAdditionalTitle = "",
) {
	return {
		type: uiConstants.SHOW_SPINNER_OVERLAY,
		isOverlayTransparent,
		spinnerOverlayAdditionalTitle,
	};
}

function hideSpinnerOverlay() {
	return {
		type: uiConstants.HIDE_SPINNER_OVERLAY,
	};
}

export function showModal<T>(modalType: string, modalState?: T) {
	return {
		type: uiConstants.PUSH_MODAL,
		modal: {
			...modalState,
			show: true,
			modalType,
		},
	};
}

export function hideModal(modalType: string) {
	return {
		type: uiConstants.POP_MODAL,
		modalType,
	};
}

export function showModalEnsureSingleInstance(
	modalType: string,
	modalState = undefined,
) {
	return (dispatch, getState) => {
		const state = getState();
		const modals = getModals(state);
		const counter = fp.countBy({ modalType }, modals);

		// On Review
		if (!counter.true || counter.true === 0) {
			dispatch(showModal(modalType, modalState));
		}
	};
}

export function hideModalEnsureSingleInstance(modalType: string) {
	return (dispatch, getState) => {
		const state = getState();
		const modals = getModals(state);
		const counter = fp.countBy({ modalType }, modals);

		// On Review
		if (counter.true !== undefined && counter.true > 0) {
			dispatch(hideModal(modalType));
		}
	};
}

function showSessionExpirationModal() {
	return showModalEnsureSingleInstance("SESSION_EXPIRATION");
}

function hideSessionExpirationModal() {
	return hideModalEnsureSingleInstance("SESSION_EXPIRATION");
}

function showServerErrorModal(response) {
	return showModalEnsureSingleInstance("SERVER_ERROR", response);
}

function hideServerErrorModal() {
	return hideModalEnsureSingleInstance("SERVER_ERROR");
}

function showNoConnectionModal() {
	return showModalEnsureSingleInstance("NO_CONNECTION");
}

function hideNoConnectionModal() {
	return hideModalEnsureSingleInstance("NO_CONNECTION");
}

function setIsOnline(isOnline: boolean) {
	return {
		type: uiConstants.SET_IS_ONLINE,
		isOnline,
	};
}

export type ShowErrorNotification = typeof showErrorNotification;
export type ShowSpinnerOverlay = typeof showSpinnerOverlay;
export type HideSpinnerOverlay = typeof hideSpinnerOverlay;
export type ShowModalType = typeof showModal;
